// ─── PREVIEW RENDER ────────────────────────────────────────────────────────────
//
// Split out of product-ui.js (further Phase 5 decomposition, per the
// vinylpac-refactoring-guide skill's "default to shared components, not one
// big file" principle). Covers the preview watermark hint, finish toggle,
// accordion, artwork upload handling, die-cut preview, and all the
// canvas-based material-effect renderers (glitter/holo/clear/mirror/basic)
// plus showArtworkPreview() -- the "what does the preview look like"
// concerns, as opposed to product-config.js's "what's being configured"
// concerns.
//
// Loaded via <script src> immediately after product-config.js, in the
// position product-ui.js used to occupy -- BEFORE the remaining inline
// block containing INIT. The only load-time work here is initLaminateChips()
// (paints the laminate chip swatches on DOMContentLoaded); everything else
// is function declarations + one var initializer, so load order relative
// to product-config.js doesn't matter; both just need to be in place
// before INIT (or any user interaction) calls into either of them.
//
// Depends on globals declared in product-state.js / pricing-engine.js /
// configurator.html's own script, and calls into product-config.js's
// updatePrice()/switchCategory()-adjacent helpers where relevant.

// ─── PREVIEW HINT ────────────────────────────────────────────────────────────
function setPreviewHint(visible) {
  var h = document.getElementById('previewHint');
  if (h) h.classList.toggle('hidden', !visible);
}

// ─── FINISH TOGGLE (laminate chip row) ───────────────────────────────────────
//
// Markup: #finishToggle > button.lam-chip[data-lam][data-sf?] > canvas.lam-swatch + span
//   Gloss  -> setFinish('glossy')          (currentSpecialty resets to 'none')
//   Matte  -> setFinish('matte')           (currentSpecialty resets to 'none')
//   Satin / Galactic (glitter-lam-*) / Spectrum (holo-lam-*) -> setSpecialtyFinish(id)
//     (currentFinish is left as-is; that is how pricing multipliers work today)
// Chips are nested in three .lam-group sub-rows (Standard / Galactic Series /
// Spectrum); everything below walks `.lam-chip` descendants so nesting is fine.
// Exactly one chip is .active at a time.

// Laminate id used by the chips: 'gloss'|'matte'|'satin'|'glitter-lam-*'|'holo-lam-*'.
function _activeLaminateChipKey() {
  // Embossed (Josh 10/15): no laminate, so no tile is picked; the row sits
  // greyed on its opening Gloss · Matte · Cosmic Shift · More view.
  if (currentEmboss !== 'none') return 'none';
  if (typeof currentSpecialty === 'string' && currentSpecialty !== 'none') return currentSpecialty;
  return currentFinish === 'matte' ? 'matte' : 'gloss';
}

function _syncLaminateChips() {
  var row = document.getElementById('finishToggle');
  if (!row) return;
  var active = _activeLaminateChipKey();
  row.querySelectorAll('.lam-chip:not(.lam-more)').forEach(function(b) {
    var key = b.getAttribute('data-sf') || b.getAttribute('data-lam');
    var on = key === active;
    b.classList.toggle('active', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
}

// Laminate card opens on Gloss / Matte / More so shoppers aren't swamped;
// More shows every laminate, Less folds it back (a picked extra stays visible).
function setLaminateMore(open) {
  var sec = document.getElementById('finishSection');
  if (!sec) return;
  if (open && sec.classList.contains('lam-off')) return;  // embossing on: no laminate list
  // Opening runs the options column long; hold the preview at its size
  // (same lock the Size / Quantity lists use).
  if (open && typeof sqPreviewLock === 'function') sqPreviewLock();
  sec.classList.toggle('lam-open', !!open);
  var more = sec.querySelector('.lam-more');
  if (more) more.setAttribute('aria-expanded', open ? 'true' : 'false');
  if (!open && typeof sqPreviewUnlock === 'function') sqPreviewUnlock();
  // Josh 10/09: one thing open at a time. Opening the laminate list folds
  // Size and Quantity to their summaries so the card never grows.
  if (open) {
    if (typeof closeSizePopup === 'function' && document.querySelector('#sqSizePopup.open')) closeSizePopup();
    if (typeof closeQtyPopup === 'function' && document.querySelector('#sqQtyPopup.open')) closeQtyPopup();
  }
}

// Re-render the finish on the 2D preview (die-cut canvas re-render, else CSS overlay).
function _refreshFinishPreview() {
  if ((artworkDataUrl || currentDefaultBaseImage) && isDieCutProduct()) {
    renderDieCutPreview(); // internally calls _applyFinishOverlay for non-glitter
  } else {
    _applyFinishOverlay();
  }
}

// Nudge the 3D sticker viewer (same guarded pattern setEmboss uses).
function _syncLaminate3D() {
  try {
    if (window.vpStickerPreview && typeof window.vpStickerPreview.sync === 'function') {
      window.vpStickerPreview.sync();
    }
  } catch (e) {}
}

function setSpecialtyFinish(val) {
  // A chip marked hidden in configurator.html (out of stock) can't come back
  // through a ?lam= link, saved product state or a cart-item edit either.
  var outOfStock = Array.prototype.some.call(document.querySelectorAll('.lam-chip[hidden]'), function(b) { return b.dataset.sf === val; });
  currentSpecialty = (!outOfStock && val) || 'none';
  if (currentSpecialty !== 'none') {
    currentEmboss = 'none';
    currentFinish = 'glossy';
  }
  updateEmbossSection();
  _syncLaminateChips();
  updatePrice();
  _refreshFinishPreview();
  _syncLaminate3D();
}

function setFinish(f) {
  currentFinish = f === 'matte' ? 'matte' : 'glossy';
  currentEmboss = 'none';
  currentSpecialty = 'none';
  updateEmbossSection();
  _syncLaminateChips();
  updatePrice();
  // Refresh finish on the preview (#64)
  _refreshFinishPreview();
  _syncLaminate3D();
}

// ── Laminate chip swatches ──────────────────────────────────────────────────
// Draws the ~22px round swatch inside each laminate chip. Canvas is 44×44
// (2× for retina) and CSS-scaled to 22px. Deterministic sparkle placement so
// swatches don't reshuffle on every redraw.
function drawLaminateSwatch(canvas, lamId) {
  if (!canvas || !canvas.getContext) return;
  var S = canvas.width || 44;
  var ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, S, S);
  var seed = 7;
  function rnd() { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; }
  function base(c0, c1) {
    var g = ctx.createRadialGradient(S*0.35, S*0.32, 0, S*0.5, S*0.5, S*0.75);
    g.addColorStop(0, c0); g.addColorStop(1, c1);
    ctx.fillStyle = g; ctx.fillRect(0, 0, S, S);
  }
  function specular(alpha, spread) {
    var hl = ctx.createRadialGradient(S*0.30, S*0.26, 0, S*0.30, S*0.26, S*spread);
    hl.addColorStop(0, 'rgba(255,255,255,' + alpha + ')');
    hl.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = hl; ctx.fillRect(0, 0, S, S);
  }
  function sparkle(colors, n, maxR) {
    for (var i = 0; i < n; i++) {
      ctx.beginPath();
      ctx.arc(rnd()*S, rnd()*S, rnd()*maxR + 0.4, 0, Math.PI*2);
      ctx.fillStyle = colors[Math.floor(rnd()*colors.length)];
      ctx.fill();
    }
  }
  function stars(color, n) {
    for (var i = 0; i < n; i++) {
      var x = rnd()*S, y = rnd()*S, r = rnd()*2.2 + 1.2;
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI*2);
      ctx.fillStyle = color; ctx.fill();
      ctx.beginPath(); ctx.arc(x, y, r*0.45, 0, Math.PI*2);
      ctx.fillStyle = 'rgba(255,255,255,0.95)'; ctx.fill();
    }
  }
  // Spectrum: soft diagonal rainbow band (transparent holographic film look)
  function rainbowBand(alpha, x0, y0, x1, y1) {
    var rg = ctx.createLinearGradient(x0, y0, x1, y1);
    var hues = ['#ff6b6b', '#ffb84d', '#ffe75c', '#5ee27a', '#4fc3ff', '#7b6bff', '#e06bff'];
    for (var i = 0; i < hues.length; i++) rg.addColorStop(i / (hues.length - 1), hues[i]);
    ctx.globalAlpha = alpha; ctx.fillStyle = rg; ctx.fillRect(0, 0, S, S); ctx.globalAlpha = 1;
  }
  // Spectrum Fracture: crackle web -- seed points joined to their two nearest neighbours
  function crackle(n, light, dark) {
    var pts = [];
    for (var i = 0; i < n; i++) pts.push([rnd()*S, rnd()*S]);
    ctx.lineCap = 'round';
    for (var a = 0; a < n; a++) {
      var d = [];
      for (var b = 0; b < n; b++) if (b !== a) {
        var dx = pts[b][0]-pts[a][0], dy = pts[b][1]-pts[a][1];
        d.push([dx*dx+dy*dy, b]);
      }
      d.sort(function(p, q) { return p[0]-q[0]; });
      for (var k = 0; k < 2 && k < d.length; k++) {
        var p2 = pts[d[k][1]];
        var mx = (pts[a][0]+p2[0])/2 + (rnd()-0.5)*3, my = (pts[a][1]+p2[1])/2 + (rnd()-0.5)*3;
        ctx.beginPath(); ctx.moveTo(pts[a][0], pts[a][1]); ctx.lineTo(mx, my); ctx.lineTo(p2[0], p2[1]);
        ctx.strokeStyle = dark; ctx.lineWidth = 1.6; ctx.stroke();
        ctx.strokeStyle = light; ctx.lineWidth = 0.7; ctx.stroke();
      }
    }
  }
  // Spectrum Hex: honeycomb lattice
  function hexGrid(r, light, dark) {
    var w = Math.sqrt(3)*r, h = 1.5*r;
    for (var row = -1; row * h < S + r; row++) {
      for (var col = -1; col * w < S + w; col++) {
        var cx = col*w + (row % 2 ? w/2 : 0), cy = row*h;
        ctx.beginPath();
        for (var i = 0; i < 6; i++) {
          var ang = Math.PI/6 + i*Math.PI/3;
          var px = cx + r*Math.cos(ang), py = cy + r*Math.sin(ang);
          if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.strokeStyle = dark; ctx.lineWidth = 1.4; ctx.stroke();
        ctx.strokeStyle = light; ctx.lineWidth = 0.6; ctx.stroke();
      }
    }
  }

  if (lamId === 'matte') {
    // Flat warm grey, faint grain, no highlight
    ctx.fillStyle = '#d2d2d2'; ctx.fillRect(0, 0, S, S);
    sparkle(['rgba(0,0,0,0.05)', 'rgba(255,255,255,0.10)'], 120, 0.6);
  } else if (lamId === 'satin') {
    // Soft sheen: warm white with a broad, diffuse diagonal band
    base('#f0f0f0', '#c8c8c8');
    var sb = ctx.createLinearGradient(0, S, S, 0);
    sb.addColorStop(0,    'rgba(255,255,255,0)');
    sb.addColorStop(0.42, 'rgba(255,255,255,0.35)');
    sb.addColorStop(0.58, 'rgba(255,255,255,0.35)');
    sb.addColorStop(1,    'rgba(255,255,255,0)');
    ctx.fillStyle = sb; ctx.fillRect(0, 0, S, S);
    specular(0.25, 0.8);
  } else if (lamId === 'glitter-lam-silver' || lamId === 'glitter-silver') {
    // Sparkle dots on a near-clear base
    base('#f2f4f7', '#c9d0d8');
    sparkle(['rgba(255,255,255,0.9)', 'rgba(205,215,228,0.85)', 'rgba(150,165,180,0.7)'], 110, 1.3);
    stars('rgba(232,238,246,0.95)', 7);
    specular(0.35, 0.7);
  } else if (lamId === 'glitter-lam-stardust' || lamId === 'glitter-stardust') {
    // Stardust: a finer, denser silver sparkle than Apollo Silver
    base('#f3f4f6', '#cdd2d9');
    sparkle(['rgba(255,255,255,0.95)', 'rgba(214,222,232,0.85)', 'rgba(160,172,186,0.7)'], 280, 0.75);
    stars('rgba(240,244,250,0.95)', 4);
    specular(0.3, 0.7);
  } else if (lamId === 'glitter-lam-gold' || lamId === 'glitter-gold') {
    base('#f9efd2', '#d9b866');
    sparkle(['rgba(255,244,200,0.95)', 'rgba(226,178,60,0.9)', 'rgba(170,120,25,0.8)'], 110, 1.3);
    stars('rgba(255,226,120,0.95)', 7);
    specular(0.35, 0.7);
  } else if (lamId === 'glitter-lam-rainbow' || lamId === 'glitter-rainbow') {
    // Galactic Cosmic Shift: holographic colour-shifting flakes
    base('#f4f2f7', '#d3d0dc');
    sparkle(['#ff5fa2', '#ffd23f', '#3ee6c1', '#4f8bff', '#b46bff', '#ff8c42'], 120, 1.3);
    stars('rgba(255,255,255,0.9)', 6);
    specular(0.3, 0.7);
  } else if (lamId === 'glitter-lam-red' || lamId === 'glitter-red') {
    // Galactic Mars Red: red metal flake on a clear (pale pink) base
    base('#fbeeee', '#e3a3a3');
    sparkle(['rgba(255,255,255,0.9)', 'rgba(232,58,58,0.9)', 'rgba(178,24,36,0.85)', 'rgba(255,140,140,0.8)'], 120, 1.3);
    stars('rgba(255,120,120,0.95)', 7);
    specular(0.35, 0.7);
  } else if (lamId === 'glitter-lam-neptune' || lamId === 'glitter-neptune') {
    // Galactic Neptune: cyan / purple flake
    base('#eef4fb', '#b7c3e4');
    sparkle(['rgba(70,220,255,0.9)', 'rgba(150,90,255,0.9)', 'rgba(255,255,255,0.85)', 'rgba(50,130,255,0.75)', 'rgba(200,120,255,0.8)'], 120, 1.3);
    stars('rgba(170,225,255,0.95)', 7);
    specular(0.35, 0.7);
  } else if (lamId === 'holo-lam-fracture' || lamId === 'holo-fracture') {
    // Spectrum Fracture: transparent holo with crackle cells
    base('#f2f2f5', '#c8c9d3');
    rainbowBand(0.55, 0, S*0.15, S, S*0.85);
    crackle(11, 'rgba(255,255,255,0.9)', 'rgba(60,60,80,0.28)');
    specular(0.35, 0.7);
  } else if (lamId === 'holo-lam-prismatic' || lamId === 'holo-prismatic') {
    // Spectrum Prismatic: smooth rainbow bands
    base('#f6f6f8', '#d6d6de');
    rainbowBand(0.78, S*0.1, 0, S*0.9, S);
    specular(0.5, 0.6);
  } else if (lamId === 'holo-lam-cosmic' || lamId === 'holo-cosmic') {
    // Spectrum Super Cosmic: dense holographic flake
    base('#f0f0f4', '#cbcbd6');
    rainbowBand(0.32, 0, S*0.2, S, S*0.8);
    sparkle(['#ff5fa2', '#ffd23f', '#3ee6c1', '#4f8bff', '#b46bff', '#ffffff', '#ff8c42'], 210, 1.0);
    stars('rgba(255,255,255,0.9)', 5);
    specular(0.3, 0.7);
  } else if (lamId === 'holo-lam-hex' || lamId === 'holo-hex') {
    // Spectrum Hex: silver honeycomb cells with a rainbow tint
    base('#f0f0f3', '#c6c7d0');
    rainbowBand(0.45, 0, S*0.2, S, S*0.8);
    hexGrid(4.2, 'rgba(255,255,255,0.85)', 'rgba(70,70,90,0.3)');
    specular(0.35, 0.7);
  } else if (lamId === 'holo-lam-hearts' || lamId === 'holo-hearts') {
    // Clear film on white stock; deeper spectral outlines so the hearts read at chip size.
    ctx.save();
    ctx.beginPath(); ctx.arc(S/2, S/2, S/2, 0, Math.PI*2); ctx.clip();
    base('#fbfbfd', '#e4e7ec');
    ctx.save(); if (S !== 44) ctx.scale(S/44, S/44);
    _paintHeartsField(ctx, 44, 44*1.8, { hues: _HEARTS_CHIP_HUES, lineWidth: 0.8 });
    ctx.restore();
    specular(0.35, 0.6);
    ctx.restore();
  } else {
    // gloss (default): white with a crisp specular highlight
    base('#ffffff', '#c9c9c9');
    specular(0.95, 0.42);
    var gl2 = ctx.createLinearGradient(S*0.55, S*0.65, S, S);
    gl2.addColorStop(0, 'rgba(255,255,255,0)');
    gl2.addColorStop(1, 'rgba(255,255,255,0.55)');
    ctx.fillStyle = gl2; ctx.fillRect(0, 0, S, S);
  }
}

// Galactic chips show a patch cut from the real 3D preview (quote-3d.js laminate
// over black ink, baked by preview/lam-chip-swatches-2026-10-10-claude/bake.py),
// so the chip matches the mockup. The painted swatch shows until the image loads.
var _LAM_SWATCH_IMAGES = {
  'glitter-lam-silver': (window.VP_CDN+"laminate-swatches__glitter-silver.png?v=053209455c"),
  'glitter-lam-gold': (window.VP_CDN+"laminate-swatches__glitter-gold.png?v=5755b26d17"),
  'glitter-lam-rainbow': (window.VP_CDN+"laminate-swatches__glitter-rainbow.png?v=b78469738f")
};
// Sharper canvases for the patterned chips (swatch is 42 CSS px).
var _LAM_SWATCH_HIRES = { 'glitter-lam-silver': 1, 'glitter-lam-gold': 1, 'glitter-lam-rainbow': 1, 'glitter-lam-stardust': 1, 'holo-lam-hearts': 1 };

function _drawLaminateSwatchImage(canvas, key) {
  var src = _LAM_SWATCH_IMAGES[key];
  if (!src) return;
  var img = new Image();
  img.onload = function() {
    var S = canvas.width, ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, S, S);
    ctx.save();
    ctx.beginPath(); ctx.arc(S/2, S/2, S/2, 0, Math.PI*2); ctx.clip();
    ctx.drawImage(img, 0, 0, S, S);
    ctx.restore();
  };
  img.src = src;
}

function initLaminateChips() {
  var row = document.getElementById('finishToggle');
  if (!row) return;
  row.querySelectorAll('.lam-chip').forEach(function(b) {
    var key = b.getAttribute('data-sf') || b.getAttribute('data-lam') || 'gloss';
    var c = b.querySelector('canvas.lam-swatch');
    if (!c) return;
    if (_LAM_SWATCH_HIRES[key]) c.width = c.height = 96;
    drawLaminateSwatch(c, key);
    _drawLaminateSwatchImage(c, key);
  });
  _syncLaminateChips();
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLaminateChips);
} else {
  initLaminateChips();
}

// ─── ACCORDION ───────────────────────────────────────────────────────────────

function toggleAccordion() {
  document.getElementById('instrAccordion').classList.toggle('open');
}

// ─── ARTWORK UPLOAD ──────────────────────────────────────────────────────────

function handleUpload(input) {
  if (!input.files || !input.files[0]) return;
  var file = input.files[0];
  if (rejectOversizeProductArtwork(input, 'body')) return;
  var completeUpload = beginProductArtworkUpload('body', file.name);
  document.querySelector('.upload-label').innerHTML = '✓ <strong>' + escHtml(file.name) + '</strong>';

  var reader = new FileReader();
  reader.onerror = function() {
    if (completeUpload(null, null)) {
      document.querySelector('.upload-label').textContent = 'Could not read this file. Please choose it again.';
      updateCartButtonState();
    }
  };
  reader.onload = function(e) {
    var rawDataUrl = e.target.result;
    var isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    // Josh 10/17: any file type. One the browser can't show as it is (PDF, AI, PSD, EPS…) goes to the order as the
    // customer's original, with a PNG preview for the page (art-files.js).
    var original = VPArtFiles.needsOriginal(file) ? VPArtFiles.dataUrl(rawDataUrl, file) : null;

    function applyArtwork(previewUrl) {
      if (!completeUpload(previewUrl, original)) return;
      artworkDataUrl         = previewUrl;
      artworkOriginalDataUrl = original; // the customer's own file goes to the order

      setPreviewHint(false); // hide watermark once artwork is loaded

      // Thumbnail inside upload zone
      var prev = document.getElementById('uploadPreview');
      prev.src = artworkDataUrl;
      prev.style.display = 'block';

      // Show print file download button
      var dlBtn = document.getElementById('printDlBtn');
      if (dlBtn) dlBtn.classList.add('visible');

      // Route to appropriate preview
      if (currentCat === 'labels' && isSheetLabelProduct()) {
        renderSheetLabelPreview();
      } else if ((currentCat === 'labels' || currentCat === 'packaging') && typeof preview3D !== 'undefined') {
        preview3D.activate(artworkDataUrl);
      } else {
        showArtworkPreview();
      }
      updateCartButtonState();
    }

    if (isPdf) {
      // Render page 1 to a PNG canvas so the preview and 3D compositor work
      renderPdfPage(rawDataUrl).then(function(pngDataUrl) {
        applyArtwork(pngDataUrl || rawDataUrl); // fall back to raw if render fails
      });
    } else if (original) {
      VPArtFiles.preview(file).then(applyArtwork);
    } else {
      applyArtwork(rawDataUrl);
    }
  };
  reader.readAsDataURL(file);
}

// ─── DIE CUT PREVIEW ─────────────────────────────────────────────────────────

function artworkPreviewKey() {
  return [currentCat, currentChipId, currentMat, currentFinish, currentSpecialty, currentEmboss, artworkDataUrl, currentDefaultBaseImage].join('|');
}

function isDieCutProduct() {
  return currentChipId === 'die-cut' || currentChipId === 'emb-die-cut' || currentChipId === 'lbl-die-cut';
}

// Sheet labels: circle, oval, square, rect, rounded — rendered as 9-up grid
function isSheetLabelProduct() {
  return ['lbl-circle','lbl-oval','lbl-square','lbl-rect','lbl-rounded','lbl-roll','lbl-die-cut'].indexOf(currentChipId) !== -1;
}

function renderSheetLabelPreview() {
  if (window.vpStickerPreview && window.vpStickerPreview.supports()) {
    window.vpStickerPreview.sync();
    return;
  }
  if (!artworkDataUrl && !currentDefaultBaseImage) return;
  _cancelMaterialAnim();
  var img = new Image();
  var renderKey = artworkPreviewKey();
  img.onload = function() {
    if (renderKey !== artworkPreviewKey()) return;
    if (window.vpStickerPreview && window.vpStickerPreview.supports()) {
      window.vpStickerPreview.sync();
      return;
    }
    var S = 380;
    var canvas = document.createElement('canvas');
    canvas.width = canvas.height = S;
    var ctx = canvas.getContext('2d');

    // ── Roll labels: single-label "live proof" — artwork fills a label frame ──
    if (currentChipId === 'lbl-roll') {
      var iWr = img.naturalWidth  || img.width  || 1;
      var iHr = img.naturalHeight || img.height || 1;

      // Backing paper strip (full width, light gray)
      ctx.fillStyle = '#efefef';
      ctx.fillRect(0, 0, S, S);

      // Hint strip lines to suggest roll backing paper
      ctx.strokeStyle = '#e0e0e0';
      ctx.lineWidth = 1;
      for (var yi = 0; yi < S; yi += 28) {
        ctx.beginPath(); ctx.moveTo(0, yi); ctx.lineTo(S, yi); ctx.stroke();
      }

      // Label frame — centered, fills most of the preview area
      var lPad = 32;
      var lW = S - lPad * 2;
      var lH = S - lPad * 2;
      var lX = lPad, lY = lPad;
      var lRr = 18; // rounded corners

      // White label background
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(lX + lRr, lY);
      ctx.lineTo(lX + lW - lRr, lY);
      ctx.arcTo(lX + lW, lY,        lX + lW, lY + lRr,        lRr);
      ctx.lineTo(lX + lW, lY + lH - lRr);
      ctx.arcTo(lX + lW, lY + lH,   lX + lW - lRr, lY + lH,   lRr);
      ctx.lineTo(lX + lRr, lY + lH);
      ctx.arcTo(lX,        lY + lH,  lX, lY + lH - lRr,        lRr);
      ctx.lineTo(lX,        lY + lRr);
      ctx.arcTo(lX,        lY,        lX + lRr, lY,             lRr);
      ctx.closePath();
      ctx.fillStyle = '#fff';
      ctx.fill();
      // Soft shadow
      ctx.shadowColor = 'rgba(0,0,0,0.12)';
      ctx.shadowBlur = 12;
      ctx.shadowOffsetY = 3;
      ctx.stroke();
      ctx.restore();

      // Artwork fills the label (object-fit: contain, with padding)
      var inner = 20;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(lX + lRr, lY);
      ctx.lineTo(lX + lW - lRr, lY);
      ctx.arcTo(lX + lW, lY,        lX + lW, lY + lRr,        lRr);
      ctx.lineTo(lX + lW, lY + lH - lRr);
      ctx.arcTo(lX + lW, lY + lH,   lX + lW - lRr, lY + lH,   lRr);
      ctx.lineTo(lX + lRr, lY + lH);
      ctx.arcTo(lX,        lY + lH,  lX, lY + lH - lRr,        lRr);
      ctx.lineTo(lX,        lY + lRr);
      ctx.arcTo(lX,        lY,        lX + lRr, lY,             lRr);
      ctx.closePath();
      ctx.clip();
      var scR = Math.min((lW - inner * 2) / iWr, (lH - inner * 2) / iHr);
      var dWr = iWr * scR, dHr = iHr * scR;
      ctx.drawImage(img, lX + (lW - dWr) / 2, lY + (lH - dHr) / 2, dWr, dHr);
      ctx.restore();

      // Label border
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(lX + lRr, lY);
      ctx.lineTo(lX + lW - lRr, lY);
      ctx.arcTo(lX + lW, lY,        lX + lW, lY + lRr,        lRr);
      ctx.lineTo(lX + lW, lY + lH - lRr);
      ctx.arcTo(lX + lW, lY + lH,   lX + lW - lRr, lY + lH,   lRr);
      ctx.lineTo(lX + lRr, lY + lH);
      ctx.arcTo(lX,        lY + lH,  lX, lY + lH - lRr,        lRr);
      ctx.lineTo(lX,        lY + lRr);
      ctx.arcTo(lX,        lY,        lX + lRr, lY,             lRr);
      ctx.closePath();
      ctx.strokeStyle = 'rgba(0,0,0,0.15)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();

      // Write to productImg
      var pImgR = document.getElementById('productImg');
      pImgR.src = canvas.toDataURL();
      pImgR.style.mixBlendMode = 'normal';
      pImgR.style.opacity = '1';
      pImgR.style.visibility = 'visible';
      document.getElementById('artworkOverlay').innerHTML = '';
      setPreviewHint(false);
      return;
    }

    // Sheet background
    ctx.fillStyle = '#d8d8d8';
    ctx.fillRect(0, 0, S, S);
    // Subtle border around the sheet
    ctx.strokeStyle = '#bbb';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, S - 1, S - 1);

    // Per-shape grid config: cols × rows = label count, cellAspect = cellH/cellW
    var cols, rows, cellAspect, pad, gap;
    switch (currentChipId) {
      case 'lbl-oval':
        // 3×4 = 12, landscape cells fill the canvas vertically
        cols = 3; rows = 4; cellAspect = 0.65; pad = 14; gap = 8; break;
      case 'lbl-rect':
        // 4×3 = 12, portrait cells
        cols = 4; rows = 3; cellAspect = 1.30; pad = 10; gap = 8; break;
      case 'lbl-rounded':
        // 4×3 = 12, slightly portrait rounded-corner cells
        cols = 4; rows = 3; cellAspect = 1.10; pad = 10; gap = 8; break;
      default:
        // circle, square, die-cut: 3×3 = 9, square cells
        cols = 3; rows = 3; cellAspect = 1.00; pad = 18; gap = 10; break;
    }
    var cellW = (S - pad * 2 - gap * (cols - 1)) / cols;
    var cellH = cellW * cellAspect;
    var totalH = rows * cellH + (rows - 1) * gap;
    var padTop = Math.max(pad, (S - totalH) / 2);

    var iW = img.naturalWidth  || img.width  || 1;
    var iH = img.naturalHeight || img.height || 1;

    for (var row = 0; row < rows; row++) {
      for (var col = 0; col < cols; col++) {
        var cx = pad    + col * (cellW + gap);
        var cy = padTop + row * (cellH + gap);

        // Die-cut sheet: artwork follows its own shape — draw contain with no clip border
        if (currentChipId === 'lbl-die-cut') {
          var scale  = Math.min(cellW / iW, cellH / iH);
          var drawW  = iW * scale;
          var drawH  = iH * scale;
          var drawX  = cx + (cellW - drawW) / 2;
          var drawY  = cy + (cellH - drawH) / 2;
          ctx.drawImage(img, drawX, drawY, drawW, drawH);
          continue; // skip the shape-clip and border drawing below
        }

        ctx.save();
        ctx.beginPath();

        if (currentChipId === 'lbl-circle') {
          ctx.arc(cx + cellW / 2, cy + cellH / 2, cellW / 2, 0, Math.PI * 2);

        } else if (currentChipId === 'lbl-oval') {
          // Landscape oval: rx (horizontal) > ry (vertical)
          ctx.ellipse(cx + cellW / 2, cy + cellH / 2, cellW / 2 * 0.92, cellH / 2 * 0.92, 0, 0, Math.PI * 2);

        } else if (currentChipId === 'lbl-square') {
          ctx.rect(cx, cy, cellW, cellH);

        } else if (currentChipId === 'lbl-rect') {
          ctx.rect(cx, cy, cellW, cellH);

        } else if (currentChipId === 'lbl-rounded') {
          var rr = cellW * 0.15;
          ctx.moveTo(cx + rr, cy);
          ctx.lineTo(cx + cellW - rr, cy);
          ctx.arcTo(cx + cellW, cy, cx + cellW, cy + rr, rr);
          ctx.lineTo(cx + cellW, cy + cellH - rr);
          ctx.arcTo(cx + cellW, cy + cellH, cx + cellW - rr, cy + cellH, rr);
          ctx.lineTo(cx + rr, cy + cellH);
          ctx.arcTo(cx, cy + cellH, cx, cy + cellH - rr, rr);
          ctx.lineTo(cx, cy + rr);
          ctx.arcTo(cx, cy, cx + rr, cy, rr);
          ctx.closePath();

        } else {
          ctx.rect(cx, cy, cellW, cellH);
        }

        ctx.clip();

        // White label background inside the shape
        ctx.fillStyle = '#f8f8f8';
        ctx.fillRect(cx, cy, cellW, cellH);

        // Scale artwork to the largest rectangle that fits *inside* the shape.
        // For curved shapes (circle, oval) this is the inscribed rectangle formula;
        // for rectilinear shapes it's just the cell dimensions.
        var ar = iW / iH; // artwork aspect ratio
        var fitW, fitH;
        if (currentChipId === 'lbl-circle') {
          // Largest rect inside circle of radius cR: w²+h²=(2cR)²
          var cR = cellW / 2;
          fitH = 2 * cR / Math.sqrt(ar * ar + 1);
          fitW = ar * fitH;
        } else if (currentChipId === 'lbl-oval') {
          // Largest rect inside landscape ellipse (rx, ry): (w/(2rx))²+(h/(2ry))²=1
          var rx = cellW * 0.46, ry = cellH * 0.46;
          fitH = 1 / Math.sqrt(ar * ar / (4 * rx * rx) + 1 / (4 * ry * ry));
          fitW = ar * fitH;
        } else {
          fitW = cellW;
          fitH = cellH;
        }
        var scale  = Math.min(fitW / iW, fitH / iH);
        var drawW  = iW * scale;
        var drawH  = iH * scale;
        var drawX  = cx + (cellW - drawW) / 2;
        var drawY  = cy + (cellH - drawH) / 2;
        ctx.drawImage(img, drawX, drawY, drawW, drawH);

        ctx.restore();

        // Thin border around each label shape
        ctx.save();
        ctx.beginPath();
        if (currentChipId === 'lbl-circle') {
          ctx.arc(cx + cellW / 2, cy + cellH / 2, cellW / 2, 0, Math.PI * 2);
        } else if (currentChipId === 'lbl-oval') {
          ctx.ellipse(cx + cellW / 2, cy + cellH / 2, cellW / 2 * 0.92, cellH / 2 * 0.92, 0, 0, Math.PI * 2);
        } else if (currentChipId === 'lbl-rounded') {
          var rr2 = cellW * 0.15;
          ctx.moveTo(cx + rr2, cy);
          ctx.lineTo(cx + cellW - rr2, cy);
          ctx.arcTo(cx + cellW, cy, cx + cellW, cy + rr2, rr2);
          ctx.lineTo(cx + cellW, cy + cellH - rr2);
          ctx.arcTo(cx + cellW, cy + cellH, cx + cellW - rr2, cy + cellH, rr2);
          ctx.lineTo(cx + rr2, cy + cellH);
          ctx.arcTo(cx, cy + cellH, cx, cy + cellH - rr2, rr2);
          ctx.lineTo(cx, cy + rr2);
          ctx.arcTo(cx, cy, cx + rr2, cy, rr2);
          ctx.closePath();
        } else {
          ctx.rect(cx, cy, cellW, cellH);
        }
        ctx.strokeStyle = 'rgba(0,0,0,0.15)';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.restore();
      }
    }

    // Write to productImg (same pattern as renderDieCutPreview)
    var pImg = document.getElementById('productImg');
    pImg.src = canvas.toDataURL();
    pImg.style.mixBlendMode = 'normal';
    pImg.style.opacity = '1';
    pImg.style.visibility = 'visible';
    document.getElementById('artworkOverlay').innerHTML = '';
    setPreviewHint(false);
  };
  img.src = artworkDataUrl || currentDefaultBaseImage;
}

function _drawCheckerboard(ctx, size) {
  var tile = 10;
  for (var y = 0; y < size; y += tile) {
    for (var x = 0; x < size; x += tile) {
      ctx.fillStyle = ((Math.floor(x/tile)+Math.floor(y/tile)) % 2 === 0) ? '#d0d0d0' : '#ffffff';
      ctx.fillRect(x, y, tile, tile);
    }
  }
}

function _buildSilhouette(img, ox, oy, dw, dh, S, expandPx) {
  var sil = document.createElement('canvas');
  sil.width = sil.height = S;
  var sctx = sil.getContext('2d');
  var steps = 24;
  for (var i = 0; i < steps; i++) {
    var a = (i / steps) * Math.PI * 2;
    sctx.drawImage(img, ox + Math.cos(a) * expandPx, oy + Math.sin(a) * expandPx, dw, dh);
  }
  sctx.drawImage(img, ox, oy, dw, dh); // fill centre
  return sil;
}

function renderDieCutPreview() {
  if (!artworkDataUrl && !currentDefaultBaseImage) return;
  // Cancel any running material animation (holo) so it doesn't keep writing to artworkOverlay
  _cancelMaterialAnim();
  // Ensure productImg is visible in case holo had hidden it
  var _pImg = document.getElementById('productImg');
  if (_pImg) _pImg.style.visibility = 'visible';
  var img = new Image();
  var renderKey = artworkPreviewKey();
  img.onload = function() {
    if (renderKey !== artworkPreviewKey()) return;
    var S = 380;
    var canvas = document.createElement('canvas');
    canvas.width = canvas.height = S;
    var ctx = canvas.getContext('2d');

    // Real uploaded artwork is typically a print file with its own built-in
    // margin, so it gets extra padding here. The default base image is already
    // tightly cropped to the sticker shape (it's a marketing photo, not a print
    // file) -- adding the same padding on top shrinks it well below how it
    // displayed before this canvas rendering was applied to it.
    var pad = artworkDataUrl ? 32 : 0;
    var scale = Math.min((S - pad * 2) / img.width, (S - pad * 2) / img.height);
    var dw = img.width * scale, dh = img.height * scale;
    var ox = (S - dw) / 2,    oy = (S - dh) / 2;
    // Underlay silhouette exactly matches the artwork edge (no bleed, no cut-line ring)
    var outerSil  = _buildSilhouette(img, ox, oy, dw, dh, S, 0);

    // ── 1. Underlay (material backing) ──
    ctx.drawImage(outerSil, 0, 0);
    ctx.globalCompositeOperation = 'source-in';
    var ul = UNDERLAY_OPTIONS.find(function(u) { return u.id === currentUnderlay; }) || UNDERLAY_OPTIONS[0];
    if (ul.pattern === 'checkerboard') {
      _drawCheckerboard(ctx, S);
    } else if (ul.pattern === 'mirror') {
      // Reflective silver — linear gradient simulating a chrome mirror surface
      var mg = ctx.createLinearGradient(0, 0, S, S);
      mg.addColorStop(0.00, '#f0f4f8');
      mg.addColorStop(0.25, '#c8d0d8');
      mg.addColorStop(0.45, '#ffffff');
      mg.addColorStop(0.55, '#e0e8f0');
      mg.addColorStop(0.75, '#a0aab4');
      mg.addColorStop(1.00, '#d8e0e8');
      ctx.fillStyle = mg;
      ctx.fillRect(0, 0, S, S);
    } else if (ul.pattern === 'holo') {
      var hg = ctx.createLinearGradient(0, 0, S, S);
      hg.addColorStop(0,    '#ff6ec7');
      hg.addColorStop(0.25, '#fffb00');
      hg.addColorStop(0.5,  '#00ffff');
      hg.addColorStop(0.75, '#a855f7');
      hg.addColorStop(1,    '#ff6ec7');
      ctx.fillStyle = hg;
      ctx.fillRect(0, 0, S, S);
    } else if (ul.pattern === 'gold') {
      var gld = ctx.createLinearGradient(0, 0, S, S);
      gld.addColorStop(0.00, '#fff4c8');
      gld.addColorStop(0.35, '#e8c45a');
      gld.addColorStop(0.55, '#fff0b0');
      gld.addColorStop(1.00, '#a67a1a');
      ctx.fillStyle = gld;
      ctx.fillRect(0, 0, S, S);
    } else if (ul.pattern === 'reflective') {
      var rfg = ctx.createLinearGradient(0, 0, S, S);
      rfg.addColorStop(0.00, '#e9e9e9');
      rfg.addColorStop(0.45, '#c8c8c8');
      rfg.addColorStop(1.00, '#707070');
      ctx.fillStyle = rfg;
      ctx.fillRect(0, 0, S, S);
    } else if (ul.pattern === 'glitter') {
      // Silver glitter base
      var gg = ctx.createRadialGradient(S*0.4, S*0.4, 0, S*0.5, S*0.5, S*0.7);
      gg.addColorStop(0,   '#f0f4f8');
      gg.addColorStop(0.5, '#b0bcc8');
      gg.addColorStop(1,   '#6a7a88');
      ctx.fillStyle = gg;
      ctx.fillRect(0, 0, S, S);
      // Scatter silver sparkles
      ctx.globalCompositeOperation = 'source-atop';
      for (var gi = 0; gi < 120; gi++) {
        var gx = Math.random() * S, gy = Math.random() * S, gr = Math.random() * 2 + 0.5;
        ctx.beginPath(); ctx.arc(gx, gy, gr, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,' + (Math.random() * 0.7 + 0.3) + ')';
        ctx.fill();
      }
    } else {
      ctx.fillStyle = ul.color || '#ffffff';
      ctx.fillRect(0, 0, S, S);
    }
    ctx.globalCompositeOperation = 'source-over';

    // ── 2. Artwork on top ──
    ctx.drawImage(img, ox, oy, dw, dh);

    // ── 4. Material effect on the artwork face (glitter / holo / mirror) ──
    // IMPORTANT: always use a temp canvas clipped to the sticker's alpha shape, then
    // blend with overlay onto the main canvas.  A direct fillRect(0,0,S,S) with overlay
    // GCO paints into transparent areas (overlay on α=0 = source colour), creating the
    // rainbow/silver square around the sticker that the user sees.
    var matId = MATERIALS[currentMat].id;
    if (matId === 'glitter' || matId === 'holo' || matId === 'mirror' || matId === 'chrome' || matId === 'gold' || matId === 'reflective') {
      var eff = document.createElement('canvas');
      eff.width = eff.height = S;
      var ec = eff.getContext('2d');

      if (matId === 'glitter') {
        var gg2 = ec.createRadialGradient(S*0.4, S*0.35, 0, S*0.5, S*0.5, S*0.75);
        gg2.addColorStop(0, 'rgba(220,230,240,0.55)');
        gg2.addColorStop(1, 'rgba(140,160,180,0.35)');
        ec.fillStyle = gg2;
        ec.fillRect(0, 0, S, S);
        ec.globalCompositeOperation = 'lighter';
        for (var gi2 = 0; gi2 < 180; gi2++) {
          ec.beginPath();
          ec.arc(Math.random()*S, Math.random()*S, Math.random()*2+0.3, 0, Math.PI*2);
          ec.fillStyle = Math.random()>0.5
            ? 'rgba(255,255,255,'+(Math.random()*0.8+0.2)+')'
            : 'rgba(200,215,230,'+(Math.random()*0.7+0.3)+')';
          ec.fill();
        }
        for (var si2 = 0; si2 < 14; si2++) {
          ec.beginPath();
          ec.arc(Math.random()*S, Math.random()*S, Math.random()*3+1.2, 0, Math.PI*2);
          ec.fillStyle = 'rgba(240,248,255,0.90)';
          ec.fill();
        }
        ec.globalCompositeOperation = 'source-over';
      } else if (matId === 'holo') {
        var hg2 = ec.createLinearGradient(0, 0, S, S);
        hg2.addColorStop(0,   '#ff6ec7');
        hg2.addColorStop(0.2, '#fffb00');
        hg2.addColorStop(0.4, '#00ffea');
        hg2.addColorStop(0.6, '#a855f7');
        hg2.addColorStop(0.8, '#3b82f6');
        hg2.addColorStop(1,   '#ff6ec7');
        ec.fillStyle = hg2;
        ec.fillRect(0, 0, S, S);
      } else if (matId === 'mirror' || matId === 'chrome') {
        var mg2 = ec.createLinearGradient(0, 0, S, S);
        mg2.addColorStop(0.00, 'rgba(240,244,248,0.65)');
        mg2.addColorStop(0.25, 'rgba(180,190,200,0.40)');
        mg2.addColorStop(0.45, 'rgba(255,255,255,0.75)');
        mg2.addColorStop(0.55, 'rgba(210,220,230,0.50)');
        mg2.addColorStop(0.75, 'rgba(140,150,164,0.45)');
        mg2.addColorStop(1.00, 'rgba(200,210,220,0.60)');
        ec.fillStyle = mg2;
        ec.fillRect(0, 0, S, S);
      } else if (matId === 'gold') {
        var gd2 = ec.createLinearGradient(0, 0, S, S);
        gd2.addColorStop(0.00, 'rgba(255,244,200,0.70)');
        gd2.addColorStop(0.40, 'rgba(201,149,42,0.45)');
        gd2.addColorStop(0.70, 'rgba(255,240,176,0.60)');
        gd2.addColorStop(1.00, 'rgba(166,122,26,0.50)');
        ec.fillStyle = gd2;
        ec.fillRect(0, 0, S, S);
      } else if (matId === 'reflective') {
        var rf2 = ec.createLinearGradient(0, 0, S, S);
        rf2.addColorStop(0.00, 'rgba(233,233,233,0.65)');
        rf2.addColorStop(0.50, 'rgba(200,200,200,0.45)');
        rf2.addColorStop(1.00, 'rgba(112,112,112,0.50)');
        ec.fillStyle = rf2;
        ec.fillRect(0, 0, S, S);
      }

      // Clip effect to the sticker's opaque pixels only (no bleed into transparent area)
      ec.globalCompositeOperation = 'destination-in';
      ec.drawImage(canvas, 0, 0);
      ec.globalCompositeOperation = 'source-over';

      // Blend onto main canvas: overlay keeps white areas white, tints dark/coloured areas
      ctx.globalCompositeOperation = 'overlay';
      ctx.globalAlpha = matId === 'holo' ? 0.55 : 1.0;
      ctx.drawImage(eff, 0, 0);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    }

    // ── Specialty finish baked into canvas ──
    // For die-cut, the finish must be drawn INTO the canvas (using source-atop so
    // sparkles only land on opaque sticker pixels) rather than as a separate overlay
    // that would cover the full preview pane regardless of sticker size.
    _applyFinishToCanvas(ctx, S);

    // ── Update preview img ──
    var pImg = document.getElementById('productImg');
    pImg.src = canvas.toDataURL();
    pImg.style.mixBlendMode = 'normal';
    pImg.style.opacity = '1';
    pImg.style.visibility = 'visible';
    document.getElementById('artworkOverlay').innerHTML = '';
    // Glitter and Hearts are already clipped and baked into the sticker pixels.
    if (!_isGlitterLam(currentSpecialty) && currentSpecialty !== 'holo-lam-hearts') {
      _applyFinishOverlay();
    }
  };
  img.src = artworkDataUrl || currentDefaultBaseImage;
}

function setUnderlay(id) {
  // Kept for programmatic use — UI now drives currentUnderlay via selectMaterial()
  currentUnderlay = id;
  if ((artworkDataUrl || currentDefaultBaseImage) && isDieCutProduct()) renderDieCutPreview();
}

function updateDieCutUnderlaySection() {
  // The main Pick Your Material buttons now serve as the backing material selector.
  // This section is no longer rendered — keep it permanently hidden.
  var section = document.getElementById('dieCutUnderlaySection');
  if (section) section.style.display = 'none';
}

// ─── MATERIAL PREVIEW RENDERING ──────────────────────────────────────────────
// Canvas-based material effects for non-die-cut products.
// #58 Glitter, #59 Clear edge trace, #60 Holo foil, #64 Laminate finish.

var _materialPreviewAnim = null; // rAF handle for animated effects (holo)

function _cancelMaterialAnim() {
  if (_materialPreviewAnim) { cancelAnimationFrame(_materialPreviewAnim); _materialPreviewAnim = null; }
}

// Scale artwork to fit a SxS canvas with optional padding
function _artworkLayout(img, S, pad) {
  pad = pad || 0;
  var scale = Math.min((S - pad * 2) / img.width, (S - pad * 2) / img.height);
  var dw = img.width * scale, dh = img.height * scale;
  return { dw: dw, dh: dh, ox: (S - dw) / 2, oy: (S - dh) / 2 };
}

// Clip the current canvas content to the artwork's alpha shape.
// Anything drawn outside the artwork's pixels becomes transparent.
function _clipToArtwork(ctx, img, L) {
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(img, L.ox, L.oy, L.dw, L.dh);
  ctx.globalCompositeOperation = 'source-over';
}

// Write canvas data URL into the product image slot and clear overlay
function _setPreviewCanvas(canvas) {
  var pImg = document.getElementById('productImg');
  pImg.style.mixBlendMode = 'normal';
  pImg.style.opacity = '1';
  pImg.style.visibility = 'visible';
  pImg.style.filter = '';
  pImg.src = canvas.toDataURL();
  document.getElementById('artworkOverlay').innerHTML = '';
}

// Galactic Series (glitter-lam-*) / Spectrum (holo-lam-*) id tests.
function _isGlitterLam(sf) { return typeof sf === 'string' && sf.indexOf('glitter-lam-') === 0; }
function _isHoloLam(sf)    { return typeof sf === 'string' && sf.indexOf('holo-lam-') === 0; }

// Per-laminate sparkle palette for the 2D glitter renderers. `dot()` returns a
// random fine-particle colour, `flare()` a random bright highlight colour.
// Silver / Gold / Cosmic Shift keep their original palettes exactly.
function _glitterLamPalette(sf) {
  function pick(arr) { return arr[Math.floor(Math.random()*arr.length)]; }
  function two(a, aMin, aRng, b, bMin, bRng) {
    return function() {
      return Math.random() > 0.5
        ? a + (Math.random()*aRng + aMin) + ')'
        : b + (Math.random()*bRng + bMin) + ')';
    };
  }
  function hues(list) { return function() { return pick(list) + (Math.random()*0.9+0.1) + ')'; }; }
  function fixed(c) { return function() { return c; }; }
  switch (sf) {
    case 'glitter-lam-gold':
      return { dot: two('rgba(255,230,100,', 0.05, 0.95, 'rgba(255,200,50,', 0.20, 0.80),
               flare: fixed('rgba(255,245,170,0.98)') };
    case 'glitter-lam-rainbow':
      return { dot: hues(['rgba(255,80,80,','rgba(255,180,50,','rgba(80,220,80,','rgba(50,180,255,','rgba(200,80,255,']),
               flare: function() { return pick(['rgba(255,120,120,0.98)','rgba(120,255,120,0.98)','rgba(120,120,255,0.98)','rgba(255,255,120,0.98)']); } };
    case 'glitter-lam-red':
      return { dot: hues(['rgba(255,70,70,','rgba(220,30,40,','rgba(255,150,150,','rgba(255,255,255,']),
               flare: function() { return pick(['rgba(255,140,140,0.98)','rgba(255,200,200,0.98)','rgba(255,90,90,0.98)']); } };
    case 'glitter-lam-neptune':
      return { dot: hues(['rgba(70,220,255,','rgba(150,90,255,','rgba(50,130,255,','rgba(200,120,255,','rgba(255,255,255,']),
               flare: function() { return pick(['rgba(150,240,255,0.98)','rgba(190,150,255,0.98)','rgba(255,255,255,0.98)']); } };
    default: // glitter-lam-silver
      return { dot: two('rgba(255,255,255,', 0.05, 0.95, 'rgba(210,225,240,', 0.20, 0.80),
               flare: fixed('rgba(255,255,255,0.98)') };
  }
}

// Paint the glitter particle field for `sf` onto a 2D context of size S.
function _paintGlitterField(tc, S, sf) {
  var pal = _glitterLamPalette(sf);
  // Dense fine particles
  for (var gli = 0; gli < 480; gli++) {
    tc.beginPath();
    tc.arc(Math.random()*S, Math.random()*S, Math.random()*3.0+0.4, 0, Math.PI*2);
    tc.fillStyle = pal.dot();
    tc.fill();
  }
  // Bright flare highlights
  tc.globalCompositeOperation = 'lighter';
  for (var gsi = 0; gsi < 40; gsi++) {
    tc.beginPath();
    tc.arc(Math.random()*S, Math.random()*S, Math.random()*5+2.0, 0, Math.PI*2);
    tc.fillStyle = pal.flare();
    tc.fill();
  }
  tc.globalCompositeOperation = 'source-over';
}

// The reference film scatters pointed hearts, small rings and diffraction flecks.
var _HEARTS_TILE_MM = 44;
var _HEARTS_HUES = ['#f39ccc', '#edca76', '#97daba', '#8ecbf0', '#c1a8ed', '#eeb5db'];
var _HEARTS_CHIP_HUES = ['#e2559c', '#d99a1c', '#2fae84', '#2f8fd8', '#8059d2', '#cf4fa6'];
var _HEARTS_OUTLINE = 'M0,-.18 C-.19,-.49 -.50,-.27 -.37,-.02 ' +
  'C-.29,.17 -.11,.40 0,.61 C.11,.40 .29,.17 .37,-.02 ' +
  'C.50,-.27 .19,-.49 0,-.18 Z';

function _traceLaminateHeart(tc, x, y, r) {
  tc.beginPath();
  tc.moveTo(x, y - r*0.18);
  tc.bezierCurveTo(x - r*0.19, y - r*0.49, x - r*0.50, y - r*0.27, x - r*0.37, y - r*0.02);
  tc.bezierCurveTo(x - r*0.29, y + r*0.17, x - r*0.11, y + r*0.40, x, y + r*0.61);
  tc.bezierCurveTo(x + r*0.11, y + r*0.40, x + r*0.29, y + r*0.17, x + r*0.37, y - r*0.02);
  tc.bezierCurveTo(x + r*0.50, y - r*0.27, x + r*0.19, y - r*0.49, x, y - r*0.18);
  tc.closePath();
}

function _heartPatternNoise(col, row, salt) {
  var n = ((col*37 + row*101 + salt*17 + 8192) % 997 + 997) % 997;
  return ((n*n*13 + n*53 + 17) % 997) / 997;
}

var _heartsLaminateMotifs = null;
function _heartLaminateMotifs() {
  if (_heartsLaminateMotifs) return _heartsLaminateMotifs;
  var motifs = [];
  [
    { cells: 2, salt: 11, min: 6.8, max: 10.2 },
    { cells: 5, salt: 37, min: 2.6, max: 4.4 },
    { cells: 10, salt: 71, min: 1.0, max: 1.8 }
  ].forEach(function(layer, level) {
    var cell = _HEARTS_TILE_MM / layer.cells;
    for (var row = 0; row < layer.cells; row++) {
      for (var col = 0; col < layer.cells; col++) {
        var n = function(salt) { return _heartPatternNoise(col, row, layer.salt + salt); };
        if (level > 0 && n(3) < (level === 1 ? 0.18 : 0.12)) continue;
        var kind = 'heart', choice = n(19);
        if (level === 2) kind = choice < 0.42 ? 'heart' : choice < 0.64 ? 'ring' :
          choice < 0.85 ? 'halo' : choice < 0.95 ? 'chip' : 'dot';
        motifs.push({
          kind: kind, level: level,
          x: (col + 0.5 + (n(5) - 0.5)*0.48)*cell,
          y: (row + 0.5 + (n(7) - 0.5)*0.48)*cell,
          size: layer.min + n(11)*(layer.max - layer.min),
          angle: (n(13) - 0.5)*(kind === 'heart' ? 0.12 : 0.8),
          nested: kind === 'heart' && level < 2 && n(29) > (level === 0 ? 0.70 : 0.90),
          hue: Math.floor(n(17)*_HEARTS_HUES.length),
          alpha: 0.65 + n(23)*0.35
        });
      }
    }
  });
  var spaced = [];
  motifs.forEach(function(m) {
    var overlap = spaced.some(function(large) {
      if (large.kind !== 'heart' || large.level >= m.level) return false;
      var dx = m.x - large.x, dy = m.y - large.y - large.size*0.08;
      dx -= Math.round(dx/_HEARTS_TILE_MM)*_HEARTS_TILE_MM;
      dy -= Math.round(dy/_HEARTS_TILE_MM)*_HEARTS_TILE_MM;
      var rx = large.size*0.40 + m.size*0.25;
      var ry = large.size*0.50 + m.size*0.25;
      return dx*dx/(rx*rx) + dy*dy/(ry*ry) < 1;
    });
    if (!overlap) spaced.push(m);
  });
  _heartsLaminateMotifs = spaced;
  return spaced;
}

function _heartAccentPath(kind) {
  return kind === 'heart' ? _HEARTS_OUTLINE : 'M-.16,-.16 L.16,-.16 L.16,.16 L-.16,.16 Z';
}

function _heartsLaminateRepeat(S) {
  var dims = typeof vpGetCurrentDims === 'function' ? vpGetCurrentDims() : { w: 3, h: 3 };
  return S * _HEARTS_TILE_MM / (Math.max(Number(dims.w) || 3, Number(dims.h) || 3)*25.4);
}

function _paintHeartsField(tc, S, repeat, opts) {
  repeat = repeat || _heartsLaminateRepeat(S);
  var hues = (opts && opts.hues) || _HEARTS_HUES;
  var scale = repeat / _HEARTS_TILE_MM;
  var motifs = _heartLaminateMotifs();
  tc.save();
  tc.lineJoin = tc.lineCap = 'round';
  // Wrapped copies retain complete outlines at a tile boundary.
  for (var row = -1; row*repeat < S + repeat; row++) {
    for (var col = -1; col*repeat < S + repeat; col++) {
      motifs.forEach(function(m) {
        var x = col*repeat + m.x*scale, y = row*repeat + m.y*scale, size = m.size*scale;
        if (x + size < 0 || y + size < 0 || x - size > S || y - size > S) return;
        tc.save(); tc.translate(x, y); tc.rotate(m.angle);
        var rg = tc.createLinearGradient(-size*0.45, -size*0.35, size*0.45, size*0.5);
        rg.addColorStop(0, hues[m.hue]);
        rg.addColorStop(1, hues[(m.hue + 1) % hues.length]);
        tc.strokeStyle = tc.fillStyle = rg;
        tc.globalAlpha = m.alpha;
        tc.lineWidth = (opts && opts.lineWidth) || Math.max(S <= 44 ? 0.55 : 0.24, scale*0.12);
        if (m.kind === 'heart') _traceLaminateHeart(tc, 0, 0, size);
        else if (m.kind === 'ring' || m.kind === 'dot') {
          tc.beginPath(); tc.arc(0, 0, size*(m.kind === 'dot' ? 0.14 : 0.36), 0, Math.PI*2);
        } else if (m.kind === 'chip') {
          tc.beginPath(); tc.rect(-size*0.16, -size*0.16, size*0.32, size*0.32);
        } else {
          tc.setLineDash([size*0.18, size*0.1734]);
          tc.beginPath(); tc.arc(0, 0, size*0.45, 0, Math.PI*2); tc.stroke();
          tc.setLineDash([]);
          tc.beginPath(); tc.arc(0, 0, size*0.28, 0, Math.PI*2);
        }
        if (m.kind === 'dot' || m.kind === 'chip') tc.fill(); else tc.stroke();
        if (m.nested) {
          _traceLaminateHeart(tc, 0, size*0.08, size*0.36); tc.stroke();
        }
        tc.restore();
      });
    }
  }
  tc.restore();
}

var _heartsLaminateSvgUrl = null;
function _heartsLaminateOverlayBg() {
  if (!_heartsLaminateSvgUrl) {
    var svg = '<svg xmlns="http://www.w3.org/2000/svg" width="440" height="440" viewBox="0 0 44 44">';
    svg += '<defs>';
    for (var i = 0; i < _HEARTS_HUES.length; i++) {
      svg += '<linearGradient id="h' + i + '" x1="0" y1="0" x2="1" y2="1">' +
        '<stop stop-color="' + _HEARTS_HUES[i] + '"/><stop offset="1" stop-color="' +
        _HEARTS_HUES[(i + 1) % _HEARTS_HUES.length] + '"/></linearGradient>';
    }
    svg += '</defs>';
    _heartLaminateMotifs().forEach(function(m) {
      for (var oy = -44; oy <= 44; oy += 44) {
        for (var ox = -44; ox <= 44; ox += 44) {
          var x = m.x + ox, y = m.y + oy;
          if (x + m.size < 0 || y + m.size < 0 || x - m.size > 44 || y - m.size > 44) continue;
          svg += '<g transform="translate(' + x + ' ' + y + ') rotate(' + (m.angle*180/Math.PI) +
            ') scale(' + m.size + ')" opacity="' + m.alpha + '" fill="none" stroke="url(#h' + m.hue +
            ')" stroke-width="' + (0.12/m.size) + '" stroke-linecap="round" stroke-linejoin="round">';
          if (m.kind === 'ring' || m.kind === 'dot') {
            svg += '<circle r="' + (m.kind === 'dot' ? 0.14 : 0.36) + '"' +
              (m.kind === 'dot' ? ' fill="url(#h' + m.hue + ')" stroke="none"' : '') + '/>';
          } else if (m.kind === 'halo') {
            svg += '<circle r=".45" stroke-dasharray=".18 .1734"/><circle r=".28"/>';
          } else {
            svg += '<path d="' + _heartAccentPath(m.kind) + '"' +
              (m.kind === 'chip' ? ' fill="url(#h' + m.hue + ')" stroke="none"' : '') + '/>';
            if (m.nested) svg += '<path d="' + _HEARTS_OUTLINE + '" transform="translate(0 .08) scale(.36)" stroke-width="' + (0.12/(m.size*0.36)) + '"/>';
          }
          svg += '</g>';
        }
      }
    });
    svg += '</svg>';
    _heartsLaminateSvgUrl = "url('data:image/svg+xml," + encodeURIComponent(svg).replace(/'/g, '%27') + "')";
  }
  var dims = typeof vpGetCurrentDims === 'function' ? vpGetCurrentDims() : { w: 3 };
  var width = 100*_HEARTS_TILE_MM / ((Number(dims.w) || 3)*25.4);
  return _heartsLaminateSvgUrl + ' 0 0 / ' + width + '% auto repeat';
}

// CSS background for the Spectrum (holo-lam-*) 2D overlay: a soft rainbow
// gradient. Fracture / Hex reuse the Prismatic look with a different angle
// (cheap: one div, no canvas); Super Cosmic gets a tighter repeat.
function _holoLamOverlayBg(sf) {
  if (sf === 'holo-lam-hearts') return _heartsLaminateOverlayBg();
  var stops = 'rgba(255,107,107,.9) 0%,rgba(255,184,77,.9) 17%,rgba(255,231,92,.9) 34%,' +
              'rgba(94,226,122,.9) 50%,rgba(79,195,255,.9) 67%,rgba(123,107,255,.9) 84%,rgba(224,107,255,.9) 100%';
  if (sf === 'holo-lam-cosmic') return 'repeating-linear-gradient(115deg,' + stops.replace(/(\d+)%/g, function(m, p) { return (p * 0.45) + '%'; }) + ')';
  var ang = sf === 'holo-lam-fracture' ? '160deg' : sf === 'holo-lam-hex' ? '25deg' : '120deg';
  return 'linear-gradient(' + ang + ',' + stops + ')';
}

// Bake a specialty finish directly onto a canvas context.
// Uses source-atop so the finish only lands on the canvas's existing opaque pixels
// (i.e. the sticker shape), never outside it. Used by renderDieCutPreview.
function _applyFinishToCanvas(ctx, S) {
  var sf = currentSpecialty;
  var hearts = sf === 'holo-lam-hearts';
  if (!_isGlitterLam(sf) && !hearts) return;

  // Build the finish on a transparent layer, then clip it to the sticker alpha.
  var tmp = document.createElement('canvas');
  tmp.width = tmp.height = S;
  var tc = tmp.getContext('2d');
  if (hearts) _paintHeartsField(tc, S);
  else _paintGlitterField(tc, S, sf);

  // source-atop leaves transparent edges and the unpatterned artwork untouched.
  ctx.globalCompositeOperation = 'source-atop';
  ctx.globalAlpha = hearts ? 0.46 : 0.60;
  ctx.drawImage(tmp, 0, 0);
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
}

// #64 — Finish/laminate overlay applied after the base artwork is set.
// Used for NON-die-cut products. Skipped while holo animation is running.
function _applyFinishOverlay() {
  if (_materialPreviewAnim) return; // holo canvas owns the overlay — don't evict it
  var overlay = document.getElementById('artworkOverlay');
  var sf = currentSpecialty;

  if (currentFinish === 'matte' || sf === 'soft-touch') {
    // Matte / Soft-touch: frosted wax sheen
    overlay.innerHTML =
      '<div style="position:absolute;inset:0;border-radius:8px;pointer-events:none;' +
      'background:rgba(255,255,255,0.18);mix-blend-mode:overlay;"></div>';
  } else if (sf === 'spot-uv' || sf === 'gloss-laminate') {
    // Gloss laminate / Spot UV: bright specular highlight
    overlay.innerHTML =
      '<div style="position:absolute;inset:0;border-radius:8px;pointer-events:none;' +
      'background:radial-gradient(ellipse at 30% 25%,rgba(255,255,255,0.55) 0%,transparent 55%);' +
      'mix-blend-mode:overlay;"></div>';
  } else if (sf === 'foil-silver') {
    overlay.innerHTML =
      '<div style="position:absolute;inset:0;border-radius:8px;pointer-events:none;' +
      'background:linear-gradient(135deg,rgba(200,220,240,0.45) 0%,rgba(255,255,255,0.55) 50%,rgba(200,220,240,0.45) 100%);' +
      'mix-blend-mode:overlay;"></div>';
  } else if (sf === 'foil-rose') {
    overlay.innerHTML =
      '<div style="position:absolute;inset:0;border-radius:8px;pointer-events:none;' +
      'background:linear-gradient(135deg,rgba(255,175,130,0.45) 0%,rgba(255,255,255,0.55) 50%,rgba(255,175,130,0.45) 100%);' +
      'mix-blend-mode:overlay;"></div>';
  } else if (_isHoloLam(sf)) {
    // Hearts retain coloured outlines on white artwork; other Spectrum looks use overlay.
    var blend = sf === 'holo-lam-hearts' ? 'normal' : 'overlay';
    overlay.innerHTML =
      '<div style="position:absolute;inset:0;border-radius:8px;pointer-events:none;' +
      'background:' + _holoLamOverlayBg(sf) + ';mix-blend-mode:' + blend + ';opacity:' +
      (sf === 'holo-lam-hearts' ? '0.46' : '0.5') + ';"></div>';
  } else if (_isGlitterLam(sf)) {
    // Galactic glitter laminate: dense sparkle particles overlaid on the finished artwork.
    // Uses a <canvas> element (not <img>) to avoid the .artwork-overlay img CSS rule
    // which would apply max-height:260px and mix-blend-mode:multiply incorrectly.
    var S = 380;
    var glc = document.createElement('canvas');
    glc.width = glc.height = S;
    // Sized like .artwork-overlay img (max-height:260px;max-width:90%) instead
    // of stretching to fill the whole flex box -- inset:0;width/height:100%
    // made this render noticeably larger than the real productImg it replaces,
    // since .artwork-overlay's box is bigger than what the img's own max-*
    // constraints would ever allow. As a normal (non-absolute) flex child,
    // the parent's align-items/justify-content:center still centers it.
    glc.style.cssText =
      'max-height:260px;max-width:90%;' +
      'mix-blend-mode:normal;opacity:0.60;pointer-events:none;border-radius:8px;';
    _paintGlitterField(glc.getContext('2d'), S, sf);
    overlay.innerHTML = '';
    overlay.appendChild(glc);
  } else {
    // Standard glossy — no overlay
    overlay.innerHTML = '';
  }
}

// #58 — Glitter vinyl material: silver glitter substrate with artwork printed on top.
// Uses temp-canvas + overlay GCO so the silver tint is invisible on white areas
// (overlay math: overlay(white, grey) = white) and only shows on coloured/dark ink.
// Sparkles are blended with 'lighter' — they add brightness on dark areas and
// are naturally invisible on already-white pixels.
function _renderGlitterPreview() {
  var img = new Image();
  var renderKey = artworkPreviewKey();
  img.onload = function() {
    if (renderKey !== artworkPreviewKey()) return;
    var S = 380;
    var canvas = document.createElement('canvas');
    canvas.width = canvas.height = S;
    var ctx = canvas.getContext('2d');
    var L = _artworkLayout(img, S, 0);

    // 1. Draw the artwork first (defines opaque area; transparent outside)
    ctx.drawImage(img, L.ox, L.oy, L.dw, L.dh);

    // 2. Silver substrate tint — via temp canvas clipped to artwork alpha,
    //    then blended with overlay so white areas stay white
    var tmp1 = document.createElement('canvas');
    tmp1.width = tmp1.height = S;
    var tc1 = tmp1.getContext('2d');
    var base = tc1.createRadialGradient(S*0.4, S*0.35, 0, S*0.5, S*0.5, S*0.75);
    base.addColorStop(0,   '#e8eef4');
    base.addColorStop(0.5, '#b0bcc8');
    base.addColorStop(1,   '#6a7a88');
    tc1.fillStyle = base;
    tc1.fillRect(0, 0, S, S);
    tc1.globalCompositeOperation = 'destination-in';
    tc1.drawImage(img, L.ox, L.oy, L.dw, L.dh);
    tc1.globalCompositeOperation = 'source-over';
    ctx.globalCompositeOperation = 'overlay';
    ctx.globalAlpha = 0.50;
    ctx.drawImage(tmp1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';

    // 3. Sparkles + star highlights — temp canvas clipped to artwork, then lighter blend
    //    'lighter' adds brightness: invisible on white (already max), visible on dark
    var tmp2 = document.createElement('canvas');
    tmp2.width = tmp2.height = S;
    var tc2 = tmp2.getContext('2d');
    for (var i = 0; i < 260; i++) {
      tc2.beginPath();
      tc2.arc(Math.random()*S, Math.random()*S, Math.random()*2+0.3, 0, Math.PI*2);
      tc2.fillStyle = Math.random() > 0.5
        ? 'rgba(255,255,255,' + (Math.random()*0.85+0.10) + ')'
        : 'rgba(200,215,230,' + (Math.random()*0.65+0.25) + ')';
      tc2.fill();
    }
    tc2.globalCompositeOperation = 'lighter';
    for (var si = 0; si < 22; si++) {
      tc2.beginPath();
      tc2.arc(Math.random()*S, Math.random()*S, Math.random()*3+1.0, 0, Math.PI*2);
      tc2.fillStyle = 'rgba(240,248,255,0.90)';
      tc2.fill();
    }
    tc2.globalCompositeOperation = 'destination-in';
    tc2.drawImage(img, L.ox, L.oy, L.dw, L.dh);
    tc2.globalCompositeOperation = 'source-over';
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = 0.70;
    ctx.drawImage(tmp2, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';

    _setPreviewCanvas(canvas);
    _applyFinishOverlay();
  };
  img.src = artworkDataUrl || currentDefaultBaseImage;
}

// #60 — Holo foil: animated rainbow shimmer over the artwork.
// Renders entirely in canvas so the shimmer never bleeds onto the preview background.
// White artwork areas stay white (overlay blend math), dark areas get rainbow colour.
function _renderHoloPreview() {
  var img = new Image();
  var renderKey = artworkPreviewKey();
  img.onload = function() {
    if (renderKey !== artworkPreviewKey()) return;
    var S = 380;
    var L = _artworkLayout(img, S, 0);

    // Hide productImg — the animated overlay canvas IS the preview
    var pImg = document.getElementById('productImg');
    pImg.style.visibility = 'hidden';

    var ov = document.getElementById('artworkOverlay');
    ov.innerHTML = '';
    var ovCanvas = document.createElement('canvas');
    ovCanvas.width = ovCanvas.height = S;
    // Sized like .artwork-overlay img (max-height:260px;max-width:90%), as a
    // normal flex child centered by .artwork-overlay's align-items/
    // justify-content:center -- NOT position:absolute;inset:0;width/height:100%.
    // That stretched the fixed 380x380 square buffer to fill the whole
    // (non-square) preview box, distorting both the artwork silhouette and
    // the holo shimmer pattern on every non-die-cut shape (Circle, Rectangle,
    // Sheets, etc. -- Die Cut never showed it because its holo effect is
    // baked into a real <img> via _applyFinishToCanvas -> pImg.src, which
    // already respects .product-preview img's own max-height/max-width).
    // A later object-fit:contain fix stopped the distortion but still
    // rendered oversized, since inset:0 gave it a bigger box than the img's
    // own max-* caps ever allow -- matching those caps directly here fixes
    // both the distortion and the size to exactly match the real productImg.
    ovCanvas.style.cssText =
      'max-height:260px;max-width:90%;pointer-events:none;border-radius:8px;';
    ov.appendChild(ovCanvas);
    var octx = ovCanvas.getContext('2d');

    // Persistent temp canvas: holo gradient clipped to artwork alpha each frame
    var tmpCanvas = document.createElement('canvas');
    tmpCanvas.width = tmpCanvas.height = S;
    var tmp = tmpCanvas.getContext('2d');

    var t = 0;
    function frame() {
      // 1. Draw artwork — this is the only opaque content; outside stays transparent
      octx.clearRect(0, 0, S, S);
      octx.drawImage(img, L.ox, L.oy, L.dw, L.dh);

      // 2. Build holo gradient on temp canvas then clip it to artwork alpha
      var angle = t * 0.6;
      var cx = S * (0.5 + 0.35 * Math.sin(t * 0.25));
      var cy = S * (0.5 + 0.35 * Math.cos(t * 0.18));
      var x1 = cx + Math.cos(angle) * S;
      var y1 = cy + Math.sin(angle) * S;
      var x2 = cx - Math.cos(angle) * S;
      var y2 = cy - Math.sin(angle) * S;
      var hg = tmp.createLinearGradient(x1, y1, x2, y2);
      hg.addColorStop(0,   '#ff6ec7');
      hg.addColorStop(0.2, '#fffb00');
      hg.addColorStop(0.4, '#00ffea');
      hg.addColorStop(0.6, '#a855f7');
      hg.addColorStop(0.8, '#3b82f6');
      hg.addColorStop(1,   '#ff6ec7');
      tmp.clearRect(0, 0, S, S);
      tmp.fillStyle = hg;
      tmp.fillRect(0, 0, S, S);
      // Clip holo to artwork alpha — outside artwork = transparent
      tmp.globalCompositeOperation = 'destination-in';
      tmp.drawImage(img, L.ox, L.oy, L.dw, L.dh);
      tmp.globalCompositeOperation = 'source-over';

      // 3. Blend clipped holo onto artwork with canvas overlay compositing:
      //    overlay(white, hue) = white — no colour on white areas
      //    overlay(dark,  hue) = vibrant holo tint on dark/coloured areas
      octx.globalCompositeOperation = 'overlay';
      octx.globalAlpha = 0.65;
      octx.drawImage(tmpCanvas, 0, 0);
      octx.globalAlpha = 1;
      octx.globalCompositeOperation = 'source-over';

      if (currentSpecialty === 'holo-lam-hearts') _applyFinishToCanvas(octx, S);
      t += 0.018;
      _materialPreviewAnim = requestAnimationFrame(frame);
    }
    frame();
  };
  img.src = artworkDataUrl || currentDefaultBaseImage;
}

// #59 — Clear: artwork + subtle checkerboard backing + edge glow trace
function _renderClearPreview() {
  var img = new Image();
  var renderKey = artworkPreviewKey();
  img.onload = function() {
    if (renderKey !== artworkPreviewKey()) return;
    var S = 380;
    var canvas = document.createElement('canvas');
    canvas.width = canvas.height = S;
    var ctx = canvas.getContext('2d');
    var L = _artworkLayout(img, S, artworkDataUrl ? 10 : 0);

    // Faint checkerboard beneath artwork to indicate transparent backing
    var tile = 12;
    for (var cy = 0; cy < S; cy += tile) {
      for (var cx = 0; cx < S; cx += tile) {
        ctx.fillStyle = ((Math.floor(cx / tile) + Math.floor(cy / tile)) % 2 === 0)
          ? 'rgba(200,200,200,0.30)' : 'rgba(255,255,255,0.30)';
        ctx.fillRect(cx, cy, tile, tile);
      }
    }

    // Clip checkerboard to artwork shape so it doesn't fill the whole square
    _clipToArtwork(ctx, img, L);

    // Draw artwork
    ctx.drawImage(img, L.ox, L.oy, L.dw, L.dh);

    // Edge glow trace: redraw with white shadow to simulate the die-cut clear border
    ctx.save();
    ctx.shadowColor  = 'rgba(255,255,255,0.95)';
    ctx.shadowBlur   = 16;
    ctx.globalAlpha  = 0.65;
    ctx.drawImage(img, L.ox, L.oy, L.dw, L.dh);
    ctx.drawImage(img, L.ox, L.oy, L.dw, L.dh); // second pass intensifies glow
    ctx.restore();

    // Final crisp artwork on top
    ctx.drawImage(img, L.ox, L.oy, L.dw, L.dh);

    _setPreviewCanvas(canvas);
    _applyFinishOverlay();
  };
  img.src = artworkDataUrl || currentDefaultBaseImage;
}

// Mirror: reflective silver gradient blended into the artwork.
// Uses temp-canvas + overlay GCO so white areas stay white (no reflective square)
// and the metallic sheen only shows on coloured/dark ink areas.
function _renderMirrorPreview() {
  var img = new Image();
  var renderKey = artworkPreviewKey();
  img.onload = function() {
    if (renderKey !== artworkPreviewKey()) return;
    var S = 380;
    var canvas = document.createElement('canvas');
    canvas.width = canvas.height = S;
    var ctx = canvas.getContext('2d');
    var L = _artworkLayout(img, S, 0);

    // 1. Draw artwork (defines opaque area)
    ctx.drawImage(img, L.ox, L.oy, L.dw, L.dh);

    // 2. Reflective gradient via temp canvas clipped to artwork alpha,
    //    blended with overlay (white areas stay white)
    var tmp = document.createElement('canvas');
    tmp.width = tmp.height = S;
    var tc = tmp.getContext('2d');
    var mg = tc.createLinearGradient(0, 0, S, S);
    mg.addColorStop(0.00, 'rgba(240,244,248,0.65)');
    mg.addColorStop(0.25, 'rgba(180,190,200,0.40)');
    mg.addColorStop(0.45, 'rgba(255,255,255,0.75)');
    mg.addColorStop(0.55, 'rgba(210,220,230,0.50)');
    mg.addColorStop(0.75, 'rgba(140,150,164,0.45)');
    mg.addColorStop(1.00, 'rgba(200,210,220,0.60)');
    tc.fillStyle = mg;
    tc.fillRect(0, 0, S, S);
    tc.globalCompositeOperation = 'destination-in';
    tc.drawImage(img, L.ox, L.oy, L.dw, L.dh);
    tc.globalCompositeOperation = 'source-over';
    ctx.globalCompositeOperation = 'overlay';
    ctx.drawImage(tmp, 0, 0);
    ctx.globalCompositeOperation = 'source-over';

    _setPreviewCanvas(canvas);
    _applyFinishOverlay();
  };
  img.src = artworkDataUrl || currentDefaultBaseImage;
}

// White vinyl: plain artwork with optional finish overlay
function _renderBasicPreview() {
  var pImg = document.getElementById('productImg');
  pImg.style.opacity = '0';
  setTimeout(function() {
    pImg.src = artworkDataUrl || currentDefaultBaseImage;
    pImg.style.mixBlendMode = 'normal';
    pImg.style.opacity = '1';
    pImg.style.filter = '';
  }, 180);
  _applyFinishOverlay();
}

function showArtworkPreview() {
  if (!artworkDataUrl && !currentDefaultBaseImage) return;
  // Sticker 3D is the finish demo — don't paint the 2D die-cut overlay over it.
  if (window.vpStickerPreview && typeof window.vpStickerPreview.sync === 'function') {
    try { window.vpStickerPreview.sync(); } catch (e) {}
    if (document.getElementById('sticker-3d')) {
      if (artworkDataUrl) setPreviewHint(false);
      return;
    }
  }
  // Only hide the "upload artwork" watermark for real uploaded artwork --
  // it's the signal that this is still just a placeholder preview, not the
  // customer's actual design, so it must stay while rendering the default
  // base image regardless of which material is selected.
  if (artworkDataUrl) setPreviewHint(false);
  _cancelMaterialAnim();
  // Holo is the only renderer that hides productImg (its animated overlay
  // canvas IS the preview). Reset it here centrally rather than in each
  // renderer -- _renderBasicPreview() previously skipped this, so switching
  // Holo -> White Vinyl left productImg permanently invisible even though
  // it had a valid src and opacity:1.
  var _pImgReset = document.getElementById('productImg');
  if (_pImgReset) _pImgReset.style.visibility = 'visible';

  // Sheet labels (including die-cut sheet) get the 9-up grid preview
  if (isSheetLabelProduct()) {
    renderSheetLabelPreview();
    return;
  }

  // Die cut stickers (non-sheet) get the canvas-based preview with underlay + cut line
  if (isDieCutProduct()) {
    renderDieCutPreview();
    return;
  }

  var mat = MATERIALS[currentMat];
  switch (mat.id) {
    case 'glitter':     _renderGlitterPreview(); break;
    case 'holo':        _renderHoloPreview();    break;
    case 'clear':       _renderClearPreview();   break;
    case 'mirror':
    case 'chrome':      _renderMirrorPreview();  break;
    case 'gold':        _renderMirrorPreview();  break;
    case 'reflective':  _renderGlitterPreview(); break;
    default:            _renderBasicPreview();   break;
  }
}
